import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useAuth } from '@/features/auth';
import { api, getNotificationConnection } from '@watchparty/shared';
const NotificationContext = createContext(null);
export function NotificationProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    const [liveNotifications, setLiveNotifications] = useState([]);
    const subscribedRef = useRef(false);
    useEffect(() => {
        if (!isAuthenticated) {
            setUnreadCount(0);
            setLiveNotifications([]);
            // Stop the hub when logging out
            if (subscribedRef.current) {
                const hub = getNotificationConnection();
                hub.off('ReceiveNotification');
                if (hub.state === HubConnectionState.Connected) {
                    hub.stop();
                }
                subscribedRef.current = false;
            }
            return;
        }
        api.get('/notifications/unread-count')
            .then((data) => setUnreadCount(data.count))
            .catch(() => { });
        const hub = getNotificationConnection();
        function handleReceiveNotification(item) {
            setUnreadCount((prev) => prev + 1);
            setLiveNotifications((prev) => [item, ...prev]);
        }
        // Remove any stale listeners before registering
        hub.off('ReceiveNotification');
        hub.on('ReceiveNotification', handleReceiveNotification);
        // Re-register listener on reconnect (auto-reconnect drops listeners on the new connection)
        hub.onreconnected(() => {
            hub.off('ReceiveNotification');
            hub.on('ReceiveNotification', handleReceiveNotification);
        });
        async function connect() {
            if (hub.state === HubConnectionState.Disconnected) {
                try {
                    await hub.start();
                }
                catch {
                    // withAutomaticReconnect will retry
                }
            }
        }
        connect();
        subscribedRef.current = true;
        return () => {
            hub.off('ReceiveNotification');
            hub.onreconnected(() => { });
            if (hub.state === HubConnectionState.Connected) {
                hub.stop();
            }
            subscribedRef.current = false;
        };
    }, [isAuthenticated]);
    const incrementUnread = useCallback(() => {
        setUnreadCount((prev) => prev + 1);
    }, []);
    const clearUnread = useCallback(() => {
        setUnreadCount(0);
        setLiveNotifications([]);
    }, []);
    return (_jsx(NotificationContext, { value: {
            unreadCount,
            setUnreadCount,
            incrementUnread,
            clearUnread,
            liveNotifications,
        }, children: children }));
}
export function useNotifications() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
}
